import { motion } from 'framer-motion';
import { UserCheck, Bell } from 'lucide-react';

// HelpCircle is not in the original imports but is used in DashboardMockup.
// For RsvpShowcase we need: UserCheck, Bell (from the notification cards)
// The original uses HelpCircle for "Maybe" notification card icon — keeping it here.
import { HelpCircle } from 'lucide-react';

export function RsvpShowcase() {
  const formFields = [
    { delay: 0.15, content: (
      <div>
        <label className="text-[10px] font-medium text-[#5d6b58] uppercase tracking-wider">Your Name</label>
        <div className="mt-1 bg-white rounded-lg px-3 py-2 border border-[#8fa382]/30 text-xs text-[#3d4a3d]">Emily Watson</div>
      </div>
    )},
    { delay: 0.3, content: (
      <div>
        <label className="text-[10px] font-medium text-[#5d6b58] uppercase tracking-wider">Will you attend?</label>
        <div className="flex gap-2 mt-1.5">
          {(['Yes', 'Maybe', 'No'] as const).map((opt) => (
            <div
              key={opt}
              className={`flex-1 text-center py-1.5 rounded-lg text-[11px] font-medium ${
                opt === 'Yes'
                  ? 'bg-[#8fa382] text-white'
                  : 'bg-white border border-[#8fa382]/30 text-[#5d6b58]'
              }`}
            >
              {opt}
            </div>
          ))}
        </div>
      </div>
    )},
    { delay: 0.45, content: (
      <div>
        <label className="text-[10px] font-medium text-[#5d6b58] uppercase tracking-wider">Dietary Requirements</label>
        <div className="mt-1 bg-white rounded-lg px-3 py-2 border border-[#8fa382]/30 text-xs text-[#3d4a3d]/40">
          e.g. Vegetarian, vegan
        </div>
      </div>
    )},
    { delay: 0.6, content: (
      <div className="bg-[#8fa382] text-white text-center py-2 rounded-lg text-xs font-semibold mt-2">
        Submit RSVP
      </div>
    )},
  ];

  return (
    <div className="flex items-center justify-center gap-6 flex-wrap md:flex-nowrap">
      {/* Phone frame with mini RSVP form */}
      <div style={{ perspective: '1000px' }}>
        <motion.div
          initial={{ rotateY: 0 }}
          whileInView={{ rotateY: 6, rotateX: 3 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl" style={{ width: 260 }}>
            <div className="bg-white rounded-[1.5rem] overflow-hidden" style={{ width: 244 }}>
              {/* Mini RSVP form */}
              <div className="p-5" style={{ backgroundColor: '#f8faf6' }}>
                <motion.div
                  className="text-center mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <p className="text-[10px] uppercase tracking-wider text-[#5d6b58] mb-1">You're Invited</p>
                  <h4 className="text-lg text-[#3d4a3d]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    Emma & James
                  </h4>
                  <p className="text-[10px] text-[#5d6b58] mt-0.5">Saturday, 14th June 2025</p>
                </motion.div>

                <div className="space-y-3">
                  {formFields.map((field, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: field.delay }}
                    >
                      {field.content}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Notification cards */}
      <div className="flex flex-col gap-3 min-w-[200px]">
        {[
          { name: 'Emily Watson', status: 'Attending', color: 'bg-success/10 text-success', icon: <UserCheck size={14} className="text-success" />, delay: 0.8 },
          { name: 'David Chen', status: 'Maybe', color: 'bg-warning/10 text-warning', icon: <HelpCircle size={14} className="text-warning" />, delay: 1.0 },
          { name: '1 reminder sent', status: '', color: 'bg-primary/10 text-primary', icon: <Bell size={14} className="text-primary" />, delay: 1.2 },
        ].map((card) => (
          <motion.div
            key={card.name}
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: card.delay, type: 'spring', stiffness: 200, damping: 20 }}
            className="flex items-center gap-3 bg-background rounded-xl px-4 py-3 border border-border shadow-sm"
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${card.color}`}>
              {card.icon}
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">{card.name}</p>
              {card.status && (
                <p className={`text-xs font-medium ${card.color.split(' ')[1]}`}>{card.status}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
